import { useCallback, useEffect, useRef, useState } from "react";

interface UseResendCooldown {
  remaining: number;
  isCoolingDown: boolean;
  start: () => void;
  reset: () => void;
}

/**
 * Tracks a simple resend cooldown timer (in seconds). Mirrors the behaviour of
 * the KMP app so resend rate limiting is consistent across surfaces.
 */
export function useResendCooldown(seconds: number): UseResendCooldown {
  const [remaining, setRemaining] = useState<number>(0);
  const intervalRef = useRef<number | null>(null);

  const stopInterval = useCallback((): void => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback((): void => {
    setRemaining(seconds);
    stopInterval();
    intervalRef.current = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          stopInterval();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [seconds, stopInterval]);

  const reset = useCallback((): void => {
    stopInterval();
    setRemaining(0);
  }, [stopInterval]);

  useEffect(() => {
    return () => {
      stopInterval();
    };
  }, [stopInterval]);

  return {
    remaining,
    isCoolingDown: remaining > 0,
    start,
    reset,
  };
}
